// Smart Suggestion Engine
// Generates context-aware suggestions for navigation and learning

import type {
  SmartSuggestion,
  FrameworkMetadata,
  SpatialLocation,
} from '../types/relationships';
import {
  relationshipGraph,
  getFrameworksByLocation,
  getFrameworksByDifficulty,
} from '../data/relationshipGraph';
import {
  findRelatedFrameworks,
  getPrerequisites,
  getNextSteps,
  findShortestPath,
} from '../types/relationships';
import { journeyTracker } from './journeyTracker';

export class SuggestionEngine {
  // Generate suggestions for a specific framework
  generateFrameworkSuggestions(frameworkId: string): SmartSuggestion[] {
    const framework = relationshipGraph.nodes.get(frameworkId);
    if (!framework) return [];

    const suggestions: SmartSuggestion[] = [];

    // 1. Spatial location suggestions
    suggestions.push(...this.getSpatialSuggestions(framework));

    // 2. Prerequisite suggestions
    suggestions.push(...this.getPrerequisiteSuggestions(framework));

    // 3. Next step suggestions
    suggestions.push(...this.getNextStepSuggestions(framework));

    // 4. Related framework suggestions
    suggestions.push(...this.getRelatedSuggestions(framework));

    // 5. Glossary term suggestions
    suggestions.push(...this.getGlossarySuggestions(framework));

    // Sort by priority
    return suggestions.sort((a, b) => b.priority - a.priority);
  }

  // Get suggestions based on user's current journey state
  generateContextualSuggestions(): SmartSuggestion[] {
    const journey = journeyTracker.getJourney();
    const suggestions: SmartSuggestion[] = [];

    // Based on current elevation
    const currentElevation = journey.mountainProgress.currentElevation;
    const elevationSuggestions = this.getElevationBasedSuggestions(currentElevation);
    suggestions.push(...elevationSuggestions);

    // Based on visited regions
    const unvisitedRegions = this.getUnvisitedRegions();
    if (unvisitedRegions.length > 0) {
      suggestions.push({
        type: 'spatial',
        title: `Explore ${unvisitedRegions.length} New Regions`,
        description: `You haven't visited ${unvisitedRegions.length} capability regions yet`,
        targetPath: '/island',
        icon: '🏝️',
        priority: 7,
        context: 'Complete island exploration',
      });
    }

    // Based on user level (derived from achievements)
    const userLevel = this.getUserLevel();
    const levelSuggestions = this.getLevelBasedSuggestions(userLevel);
    suggestions.push(...levelSuggestions);

    return suggestions.sort((a, b) => b.priority - a.priority).slice(0, 10);
  }

  // Spatial location suggestions
  private getSpatialSuggestions(framework: FrameworkMetadata): SmartSuggestion[] {
    const suggestions: SmartSuggestion[] = [];

    framework.spatialLocations.forEach(location => {
      if (location.type === 'island-region') {
        suggestions.push({
          type: 'spatial',
          title: `📍 Find this on the Island`,
          description: `${location.label} - ${location.description}`,
          targetPath: '/island',
          icon: location.icon,
          priority: 9,
          context: `Navigate to ${location.label}`,
        });
      }

      if (location.type === 'mountain-elevation') {
        const elevationLabel = this.getElevationLabel(location.id);
        suggestions.push({
          type: 'spatial',
          title: `🏔️ Climb this route`,
          description: `${elevationLabel} - ${location.description}`,
          targetPath: '/mountain',
          icon: location.icon,
          priority: 8,
          context: location.difficulty ? `Difficulty: ${location.difficulty}` : undefined,
        });
      }

      if (location.type === 'mof-layer') {
        suggestions.push({
          type: 'spatial',
          title: `🏗️ View in MOF Architecture`,
          description: `${location.label} - ${location.description}`,
          targetPath: this.getMOFLayerPath(location.id),
          icon: location.icon,
          priority: 6,
          context: 'System architecture view',
        });
      }
    });

    return suggestions;
  }

  // Prerequisite suggestions
  private getPrerequisiteSuggestions(framework: FrameworkMetadata): SmartSuggestion[] {
    if (framework.prerequisites.length === 0) return [];

    const prerequisites = getPrerequisites(relationshipGraph, framework.encodedId);
    
    return prerequisites.map(prereq => ({
      type: 'prerequisite',
      title: `📚 Foundation: ${prereq.name}`,
      description: `Recommended before ${framework.name}`,
      targetId: prereq.encodedId,
      targetPath: `/entry/${prereq.encodedId}`,
      icon: '🎯',
      priority: 10,
      context: `Difficulty: ${prereq.difficulty}`,
    }));
  }

  // Next step suggestions
  private getNextStepSuggestions(framework: FrameworkMetadata): SmartSuggestion[] {
    const nextSteps = getNextSteps(relationshipGraph, framework.encodedId);
    
    return nextSteps.slice(0, 3).map(next => ({
      type: 'next-step',
      title: `⏭️ Build on this: ${next.name}`,
      description: `Advanced concept that extends ${framework.name}`,
      targetId: next.encodedId,
      targetPath: `/entry/${next.encodedId}`,
      icon: '🚀',
      priority: 8,
      context: `Difficulty: ${next.difficulty}`,
    }));
  }

  // Related framework suggestions
  private getRelatedSuggestions(framework: FrameworkMetadata): SmartSuggestion[] {
    const related = findRelatedFrameworks(relationshipGraph, framework.encodedId, 5);
    
    return related.map(({ framework: relatedFw, relationship }) => ({
      type: 'related',
      title: `🔗 Related: ${relatedFw.name}`,
      description: relationship.description || `${this.getRelationshipLabel(relationship.type)} ${relatedFw.name}`,
      targetId: relatedFw.encodedId,
      targetPath: `/entry/${relatedFw.encodedId}`,
      icon: this.getRelationshipIcon(relationship.type),
      priority: Math.round(relationship.strength * 10),
      context: `${Math.round(relationship.strength * 100)}% relevant`,
    }));
  }

  // Glossary term suggestions
  private getGlossarySuggestions(framework: FrameworkMetadata): SmartSuggestion[] {
    if (framework.glossaryTerms.length === 0) return [];

    return [{
      type: 'glossary',
      title: `📖 ${framework.glossaryTerms.length} Related Terms`,
      description: framework.glossaryTerms.slice(0, 3).join(', ') + (framework.glossaryTerms.length > 3 ? '...' : ''),
      targetPath: '/glossary',
      icon: '📚',
      priority: 5,
      context: 'View definitions',
    }];
  }

  // Elevation-based suggestions
  private getElevationBasedSuggestions(currentElevation: string): SmartSuggestion[] {
    const suggestions: SmartSuggestion[] = [];
    
    const frameworks = getFrameworksByLocation('mountain-elevation', currentElevation);
    
    if (frameworks.length > 0) {
      suggestions.push({
        type: 'spatial',
        title: `📊 ${frameworks.length} Templates at your level`,
        description: `Frameworks available at ${this.getElevationLabel(currentElevation)}`,
        targetPath: '/templates',
        icon: '📋',
        priority: 8,
        context: `Current elevation: ${this.getElevationLabel(currentElevation)}`,
      });
    }

    return suggestions;
  }

  // Level-based suggestions
  private getLevelBasedSuggestions(userLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert'): SmartSuggestion[] {
    const suggestions: SmartSuggestion[] = [];
    
    const frameworksAtLevel = getFrameworksByDifficulty(userLevel);
    
    if (frameworksAtLevel.length > 0) {
      const sample = frameworksAtLevel[Math.floor(Math.random() * frameworksAtLevel.length)];
      suggestions.push({
        type: 'related',
        title: `🎯 Try: ${sample.name}`,
        description: `${userLevel.charAt(0).toUpperCase() + userLevel.slice(1)}-level framework`,
        targetId: sample.encodedId,
        targetPath: `/entry/${sample.encodedId}`,
        icon: '💡',
        priority: 7,
        context: `Matches your current level`,
      });
    }

    return suggestions;
  }

  // Find connection path between two frameworks
  findConnectionPath(fromId: string, toId: string): string[] {
    const path = findShortestPath(relationshipGraph, fromId, toId);
    
    if (!path) return [];
    
    return path.path.map(segment => {
      const fromFw = relationshipGraph.nodes.get(segment.from);
      const toFw = relationshipGraph.nodes.get(segment.to);
      return `${fromFw?.name} → ${this.getRelationshipLabel(segment.relationship.type)} → ${toFw?.name}`;
    });
  }

  // Calculate "degrees of separation"
  getDegreesOfSeparation(fromId: string, toId: string): number {
    const path = findShortestPath(relationshipGraph, fromId, toId);
    return path ? path.distance : -1; // -1 means no connection
  }

  // Helper methods
  private getUserLevel(): 'beginner' | 'intermediate' | 'advanced' | 'expert' {
    const journey = journeyTracker.getJourney();
    const points = journey.totalAchievementPoints;
    
    if (points < 100) return 'beginner';
    if (points < 300) return 'intermediate';
    if (points < 600) return 'advanced';
    return 'expert';
  }

  private getUnvisitedRegions(): string[] {
    const journey = journeyTracker.getJourney();
    const allRegions = [
      'clarity-focus', 'conceptual-precision', 'tool-fluency',
      'stakeholder-intel', 'cultural-reinforce', 'rhythm-ritual',
      'tradeoff-intel', 'cost-delay', 'process-discipline',
      'situational-aware', 'outcome-reflection', 'avoidance-activation',
    ];
    
    return allRegions.filter(r => !journey.islandProgress.visitedRegions.has(r as any));
  }

  private getElevationLabel(elevationId: string): string {
    const labels: Record<string, string> = {
      'base-camp': '1000m - DECIDE',
      'lower-camp': '2500m - ENGAGE',
      'middle-camp': '4000m - GATHER',
      'high-camp': '5500m - ARRANGE',
      'summit': '7000m - PRIORITIZE',
      'decide': '1000m - DECIDE',
      'engage': '2500m - ENGAGE',
      'gather': '4000m - GATHER',
      'arrange': '5500m - ARRANGE',
      'prioritize': '7000m - PRIORITIZE',
      'all': 'All Elevations (1000m-7000m)',
    };
    return labels[elevationId] || elevationId;
  }

  private getMOFLayerPath(layerId: string): string {
    const paths: Record<string, string> = {
      'engines': '/engines',
      'm3': '/grammar',
      'm2': '/models',
      'm1': '/templates',
      'm0': '/runtime',
    };
    return paths[layerId] || '/overview';
  }

  private getRelationshipLabel(type: string): string {
    const labels: Record<string, string> = {
      'prerequisite': 'builds upon',
      'complement': 'complements',
      'alternative': 'alternative to',
      'contains': 'contains',
      'extends': 'extends',
      'conflicts': 'conflicts with',
      'applies-to': 'applies to',
      'derived-from': 'derived from',
    };
    return labels[type] || type;
  }

  private getRelationshipIcon(type: string): string {
    const icons: Record<string, string> = {
      'prerequisite': '📚',
      'complement': '🤝',
      'alternative': '🔄',
      'contains': '📦',
      'extends': '🚀',
      'conflicts': '⚖️',
      'applies-to': '🎯',
      'derived-from': '🌱',
    };
    return icons[type] || '🔗';
  }
}

// Singleton instance
export const suggestionEngine = new SuggestionEngine();

